import SendIcon from '@mui/icons-material/Send';
import { Alert, Stack } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { EditorState } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import 'draft-js/dist/Draft.css';
import { useMemo, useReducer } from 'react';
import { makeStyles } from 'tss-react/mui';
import useAsyncScheduleMessage from '../../../../hooks/useAsyncScheduleMessage';
import { ApActionType } from '../../../../sic-lib/enums/ApActionType';
import { RequestStatus } from '../../../../sic-lib/enums/RequestStatus';
import { GenericReducer, genericReducer } from '../../../../sic-lib/reducers/genericReducer';
import { ContactConsent } from '../../../../sic-lib/types/Contact';
import { sanitizeAllowedTags } from '../../../../sic-lib/utils/sanitizeHtml';
import ComposeEmailFields from '../../../../types/ComposeEmailFields';
import ButtonLoading from '../../../Shared/ButtonLoading';
import ComposeEmailRaw from './ComposeEmailRaw/ComposeEmailRaw';

const useStyles = makeStyles()((theme: Theme) => ({
    root: {
        padding: theme.spacing(0, 1),
    },
    buttonWrapper: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginTop: theme.spacing(1),
    },
}));

interface Props {
    contactId: number;
    clientFieldId: number;
    consent: ContactConsent | undefined;
    userWithDialer: boolean;
    subject?: string;
}

export default function ComposeEmail({
    contactId,
    clientFieldId,
    consent,
    userWithDialer,
    subject = '',
}: Props) {
    const { classes } = useStyles();

    const { requestStatus, asyncScheduleMessage } = useAsyncScheduleMessage();

    const [composerState, composerDispatch] = useReducer<GenericReducer<ComposeEmailFields>>(
        genericReducer,
        {
            subject: subject,
            cc: '',
            bcc: '',
            editor: EditorState.createEmpty(),
        },
    );

    const consentStr = useMemo(() => {
        const expiredDate = consent?.impliedConsentExpiry
            ? ` Expired ${new Date(consent?.impliedConsentExpiry).toLocaleDateString()}.`
            : '';
        if (!consent?.expressConsent && !consent?.impliedConsent) {
            return `You do not have consent to message this contact.${expiredDate}`;
        } else if (!consent?.emailConsent) {
            return 'The contact has opted-out of Email messaging.';
        }
        if (!userWithDialer) {
            return 'Client do not have a user with Dialer.';
        }
        return undefined;
    }, [consent]);

    const handleSend = async () => {
        console.log('Sending email', composerState.subject, composerState.editor);
        const asyncResult = await asyncScheduleMessage(
            contactId,
            ApActionType.email,
            clientFieldId,
            {
                subject: composerState.subject,
                body: sanitizeAllowedTags(stateToHTML(composerState.editor.getCurrentContent())),
            },
        );

        composerDispatch({
            subject: '',
            editor: EditorState.createEmpty(),
        });
        console.log('Email sent', requestStatus);
    };

    if (!!consentStr) return <Alert severity="warning">{consentStr}</Alert>;

    return (
        <Stack spacing={1}>
            <ComposeEmailRaw
                subjectValue={composerState.subject}
                ccValue={composerState.cc}
                bccValue={composerState.bcc}
                editorState={composerState.editor}
                onUpdateHandler={composerDispatch}
                isDisabled={isNaN(clientFieldId)}
            />
            <div className={classes.buttonWrapper}>
                <ButtonLoading
                    isLoading={requestStatus === RequestStatus.active}
                    isSuccess={requestStatus === RequestStatus.success}
                    successLabel="Email Sent"
                    disabled={isNaN(clientFieldId)}
                    onClick={handleSend}
                    startIcon={<SendIcon />}
                    variant="contained"
                    color="secondary"
                >
                    Send
                </ButtonLoading>
            </div>
        </Stack>
    );
}
